import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { deleteSpareInvoice } from '../../../services/mutations';
import { computePurchaseOrderTotals } from '../spare-purchase-orders/purchaseOrderTotals';
import { supplyListMoneySpanClass, SupplyListLineItemCountCell } from '../spareListMoneyDisplay';
import type { SpareInvoice } from './useData';

export const moduleConfig = {
  module: {
    title: 'Spare invoices',
    permissionModuleKey: 'spare_invoices',
    route: {
      list: '/spares/spare-invoices',
      new: '/spares/spare-invoices/new',
      detail: (id: number) => `/spares/spare-invoices/${id}`,
      edit: (id: number) => `/spares/spare-invoices/${id}/edit`},
    api: {
      list: '/spare-invoices',
      detail: (id: number) => `/spare-invoices/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by reference, notes, case, purchase order or supplier…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['vessel', 'ref', 'invDate', 'caseRef', 'orderRef', 'supplier']},
  ],
  filters: {
    fields: [
      {
        key: 'vessel',
        label: 'Vessel',
        type: 'select' as const,
        placeholder: 'All vessels',
        getValue: (item: SpareInvoice) => item.vessel?.vesselName || '',
        options: []},
      {
        key: 'ref',
        label: 'Reference',
        placeholder: 'All references',
        getValue: (item: SpareInvoice) => item.ref || '',
        options: []},
      {
        key: 'invDate',
        label: 'Invoice date',
        type: 'date' as const,
        placeholder: 'All invoice dates',
        getValue: (item: SpareInvoice) => item.invDate || '',
        options: []},
      {
        key: 'caseRef',
        label: 'Case',
        type: 'select' as const,
        placeholder: 'All cases',
        getValue: (item: SpareInvoice) => item.spareCase?.ref || '',
        options: []},
      {
        key: 'orderRef',
        label: 'Purchase order',
        type: 'select' as const,
        placeholder: 'All purchase orders',
        getValue: (item: SpareInvoice) => item.order?.ref || '',
        options: []},
      {
        key: 'supplier',
        label: 'Supplier',
        type: 'select' as const,
        placeholder: 'All suppliers',
        getValue: (item: SpareInvoice) => item.supplier?.name || '',
        options: []},
    ],
    applyFilter: (
      items: SpareInvoice[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>,
    ) => {
      const asArray = (v: string[] | string | undefined): string[] =>
        Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : [];
      const toDateOnly = (s?: string | null): string => (s ? String(s).slice(0, 10) : '');
      const dateMatches = (
        rawDate: string | null | undefined,
        values: string[],
        range: { start: Date | null; end: Date | null } | undefined,
      ): boolean => {
        if (range?.start || range?.end) {
          const d = rawDate ? new Date(rawDate) : null;
          if (!d || Number.isNaN(d.getTime())) return false;
          if (range.start && d < range.start) return false;
          if (range.end && d > range.end) return false;
          return true;
        }
        if (!values.length) return true;
        const itemDate = toDateOnly(rawDate);
        return values.some((f) => itemDate.includes(String(f).slice(0, 10)));
      };
      return items.filter((item) => {
        const vessel = asArray(filters.vessel);
        if (vessel.length) {
          const v = (item.vessel?.vesselName || '').toLowerCase();
          if (!vessel.some((f) => v.includes(String(f).toLowerCase()))) return false;
        }

        const ref = asArray(filters.ref);
        if (ref.length) {
          const v = item.ref || '';
          if (!ref.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }

        if (!dateMatches(item.invDate, asArray(filters.invDate), dateRanges?.invDate)) return false;

        const caseRef = asArray(filters.caseRef);
        if (caseRef.length) {
          const c = (item.spareCase?.ref || '').toLowerCase();
          if (!caseRef.some((f) => c.includes(String(f).toLowerCase()))) return false;
        }

        const orderRef = asArray(filters.orderRef);
        if (orderRef.length) {
          const o = (item.order?.ref || '').toLowerCase();
          if (!orderRef.some((f) => o.includes(String(f).toLowerCase()))) return false;
        }

        const supplier = asArray(filters.supplier);
        if (supplier.length) {
          const s = (item.supplier?.name || '').toLowerCase();
          if (!supplier.some((f) => s.includes(String(f).toLowerCase()))) return false;
        }
        return true;
      });
    },
    applySearch: (items: SpareInvoice[], searchTerm: string) => {
      const q = searchTerm.toLowerCase();
      return items.filter((item) => {
        const parts = [
          item.ref,
          item.notes,
          item.paymentTerms,
          item.spareCase?.ref,
          item.order?.ref,
          String(item.id),
          String(item.supplierId),
          String(item.purchaseOrderId ?? ''),
          item.supplier?.name,
          item.supplier?.code,
          item.currency?.code,
          item.currency?.name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return parts.includes(q);
      });
    }},
  toolbar: {
    actions: []},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: SpareInvoice) => item.id,
  },
  emptyStates: {
    noData: 'Create a supply invoice to get started.',
    noSearchResults: 'No supply invoices match your search.',
    noFilterResults: 'No supply invoices match the filters.',
  },
  delete: {
    title: 'Delete supply invoice',
    message: 'Delete this invoice and all its lines?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete supply invoice.',
    handler: async (id: number) => {
      await deleteSpareInvoice(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  form: {
    new: { title: 'New supply invoice', subtitle: 'Custom form', submitText: 'Create' },
    edit: { title: 'Edit supply invoice', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.'},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

/** Path must stay in sync with `moduleConfig.route.caseView` in `spare-cases/config.tsx`. */
function supplyInvoiceCaseIdForView(row: SpareInvoice | undefined): number | null {
  const id = row?.spareCase?.id ?? row?.caseId ?? 0;
  return id > 0 ? id : null;
}

const InvoiceCaseColumnCell: React.FC<{ row: SpareInvoice }> = ({ row }) => {
  const { colors } = useTheme();
  const caseId = supplyInvoiceCaseIdForView(row);
  if (caseId == null) {
    return (
      <div className="max-w-[16rem] py-0.5">
        <span className={cn('text-sm', colors.textSecondary)}>—</span>
      </div>
    );
  }
  const displayRef = row.spareCase?.ref?.trim() || `Case #${caseId}`;
  const href = `/spares/spare-cases/${caseId}/case-view`;
  const iconBtnClass =
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900 dark:text-indigo-400 dark:hover:bg-indigo-950/50 dark:hover:text-indigo-300';

  return (
    <div className="flex max-w-[16rem] items-center gap-0.5 py-0.5">
      <Link
        to={href}
        className={iconBtnClass}
        title="Open case view"
        aria-label="Open case view"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
      </Link>
      <span className={cn('min-w-0 flex-1 truncate text-sm', colors.text)} title={displayRef}>
        {displayRef}
      </span>
    </div>
  );
};

const fmtMoney = (n: number, suffix?: string) =>
  `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}${suffix ? ` ${suffix}` : ''}`;

const InvoiceMoneyCell: React.FC<{
  invoice: SpareInvoice | undefined;
  variant: 'expenses' | 'items' | 'grand' | 'usd';
}> = ({ invoice, variant }) => {
  const { colors } = useTheme();
  if (!invoice) {
    return (
      <span className={cn('block w-full min-w-[6.75rem] text-right text-sm', colors.textSecondary)}>—</span>
    );
  }
  const t = computePurchaseOrderTotals(invoice);
  const ccy = invoice.currency?.code ?? '';
  if (variant === 'expenses') {
    return (
      <span
        className={supplyListMoneySpanClass('expenses', colors)}
        title={
          ccy
            ? `Sum of expense value + VAT in invoice currency (${ccy})`
            : 'Sum of expense value + VAT in invoice currency'
        }
      >
        {fmtMoney(t.expensesSum)}
      </span>
    );
  }
  if (variant === 'items') {
    return (
      <span
        className={supplyListMoneySpanClass('items', colors)}
        title={ccy ? `Sum of line totals in invoice currency (${ccy})` : 'Sum of line totals in invoice currency'}
      >
        {fmtMoney(t.itemsSum)}
      </span>
    );
  }
  if (variant === 'grand') {
    return (
      <span
        className={supplyListMoneySpanClass('grand', colors)}
        title={
          ccy
            ? `Items sum + expenses sum in invoice currency (${ccy})`
            : 'Items sum + expenses sum in invoice currency'
        }
      >
        {fmtMoney(t.grandTotal)}
      </span>
    );
  }
  return (
    <span
      className={supplyListMoneySpanClass('usd', colors, t.grandTotalUsd != null)}
      title="(Line totals + expenses) × exchange rate, in USD"
    >
      {t.grandTotalUsd != null ? fmtMoney(t.grandTotalUsd) : '—'}
    </span>
  );
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<SpareInvoice>[] => [
  {
    key: 'vessel.vesselName',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => (
      <TextCell
        value={
          row?.vessel?.vesselName
            ? row.vessel.vesselName
            : row?.vesselId != null
              ? String(row.vesselId)
              : ''
        }
      />
    )},
  { key: 'ref', header: 'Reference', sortable: true, render: (v, row) => <TextCell value={v ?? row?.ref} /> },
  { key: 'invDate', header: 'Invoice date', sortable: true, render: (v) => formatDate(v) },
  {
    key: 'spareCase.ref',
    header: 'Case',
    sortable: true,
    maxWidth: '16rem',
    render: (_v, row) => (row ? <InvoiceCaseColumnCell row={row} /> : <TextCell value="" />)},
  {
    key: 'order.ref',
    header: 'Purchase order',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.order?.ref ?? (row?.purchaseOrderId ? `#${row.purchaseOrderId}` : '')} />},
  {
    key: 'supplier.name',
    header: 'Supplier',
    sortable: true,
    render: (_v, row) => (
      <TextCell
        value={
          row?.supplier?.name
            ? row.supplier.name
            : row?.supplierId != null
              ? String(row.supplierId)
              : ''
        }
      />
    )},
  {
    key: 'lineItems',
    header: 'Items',
    sortable: true,
    align: 'right',
    render: (_v, row) => (
      <SupplyListLineItemCountCell count={row?._count?.lineItems ?? row?.lineItems?.length ?? 0} />
    )},
  {
    key: 'currency.code',
    header: 'CCY',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.currency?.code} />},
  {
    key: 'expensesSum',
    header: 'Expenses',
    sortable: false,
    align: 'right',
    render: (_v, row) => <InvoiceMoneyCell invoice={row} variant="expenses" />},
  {
    key: 'itemsSum',
    header: 'Items Total',
    sortable: false,
    align: 'right',
    render: (_v, row) => <InvoiceMoneyCell invoice={row} variant="items" />},
  {
    key: 'grandTotal',
    header: 'Grand Total',
    sortable: false,
    align: 'right',
    render: (_v, row) => <InvoiceMoneyCell invoice={row} variant="grand" />},
  {
    key: 'grandTotalUsd',
    header: 'Grand Total ($)',
    sortable: false,
    align: 'right',
    render: (_v, row) => <InvoiceMoneyCell invoice={row} variant="usd" />},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

const TableActionsCell: React.FC<{
  item: SpareInvoice;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => {
  const location = useLocation();
  const editReturnState = { from: `${location.pathname}${location.search}` };
  return (
    <div className="flex items-center flex-nowrap">
      <Link
        to={moduleConfig.module.route.detail(item.id)}
        className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      </Link>
      {canEdit && (
        <Link
          to={moduleConfig.module.route.edit(item.id)}
          state={editReturnState}
          className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </Link>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export const getTableActions = (
  item: SpareInvoice,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};
